import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { Organisation, UserRole } from "@/lib/api/organisation/organisation";
import { useOrganisationApi } from "@/lib/api/organisation/useOrganisationApi";
import { useAuth } from "@/lib/hooks";

type OrganisationContextType = {
  organisation: Organisation | null;
  userRole: UserRole | null;
  isLoading: boolean;
  isAdmin: boolean;
  needsOnboarding: boolean;
  refetchOrganisation: () => Promise<void>;
  setOrganisation: (org: Organisation | null) => void;
};

const OrganisationContext = createContext<OrganisationContextType | undefined>(
  undefined,
);

export const OrganisationProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { getCurrentOrganisation, getCurrentUserRole } = useOrganisationApi();

  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOrganisationData = useCallback(async () => {
    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated) {
      setIsLoading(false);

      return;
    }

    try {
      setIsLoading(true);
      const [org, roleData] = await Promise.all([
        getCurrentOrganisation(),
        getCurrentUserRole(),
      ]);

      setOrganisation(org);
      setUserRole(roleData.role);
    } catch (error) {
      console.error("Error fetching organisation data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    void fetchOrganisationData();
  }, [fetchOrganisationData]);

  const isAdmin = userRole === UserRole.ADMIN;
  const needsOnboarding = !isLoading && isAuthenticated && !organisation;

  return (
    <OrganisationContext.Provider
      value={{
        organisation,
        userRole,
        isLoading,
        isAdmin,
        needsOnboarding: needsOnboarding ?? false,
        refetchOrganisation: fetchOrganisationData,
        setOrganisation,
      }}
    >
      {children}
    </OrganisationContext.Provider>
  );
};

export const useOrganisationContext = (): OrganisationContextType => {
  const context = useContext(OrganisationContext);
  if (context === undefined) {
    throw new Error(
      "useOrganisationContext must be used within an OrganisationProvider",
    );
  }

  return context;
};
